import type { Cents } from '../../shared/money/cents.js'
import type { CivilDate } from '../../shared/date/civil.js'
import type { RecordedBy } from './recorded-by.js'
import type { BaseUnit, MilliCents, Unit } from './unit.js'

/**
 * Item do catálogo da casa — o nome **canônico** de uma coisa que compramos.
 * Transcrição de `Docs/02-Dominio/Product.md`.
 *
 * *"'Detergente Ypê 500ml' é um `Product`. `DET YPE 500ML` numa nota fiscal
 * **não é** — é um `ReceiptItem` que aponta para este produto. A distinção é o
 * que faz o catálogo funcionar."*
 *
 * O que o tipo não expressa:
 *
 * - **`stats` é derivado** de `PricePoint`, e recalculável. *"Se `stats`
 *   divergir do histórico, o histórico vence."* Quem o mantém é
 *   `onPricePointWrite`, e o recálculo é idempotente.
 * - `size` e `unit` são anuláveis porque nem todo produto tem tamanho
 *   declarado — e sem os dois **não há preço por unidade base**, então o produto
 *   entra no catálogo e fica fora das comparações. É melhor que exigir um número
 *   inventado.
 * - `normalizedName` guarda a saída de `normalizedText`, com a **versão** que a
 *   produziu ao lado. Ver `normalized-text.ts` para por que a versão importa.
 */
export type Product = {
  id: string
  householdId: string

  /** "Detergente Ypê 500ml" — canônico. */
  name: string
  /** `normalizedText(name)`, para busca e para o matching da Fase 8. */
  normalizedName: string
  /** A versão de `normalizedText` que produziu o campo acima. */
  normalizedVersion: number

  brand: string | null
  /** Categoria de gasto associada. */
  categoryId: string | null

  /**
   * A família a que esta embalagem pertence — "Coca-Cola", para a garrafa de
   * 2 L. `null` é o normal: a maioria dos produtos não tem irmão de outro
   * tamanho, e exigir família de todos seria burocracia.
   *
   * É ela que permite responder *"o que compensa por litro"* atravessando as
   * embalagens. Ver `family.ts`.
   */
  familyId: string | null

  size: number | null
  unit: Unit | null
  /** Quantas unidades no pacote. 1 é o normal. */
  packQuantity: number

  /**
   * O código de barras **como foi digitado** — a evidência, no mesmo espírito
   * de `name` ao lado de `normalizedName`. Fica guardado mesmo quando não
   * valida, para nada se perder.
   */
  barcode: string | null
  /**
   * O mesmo código **normalizado para GTIN-14**, ou `null` quando não fecha no
   * dígito verificador. É por ele que a cascata casa — UPC-A e EAN-13 do mesmo
   * produto chegam aqui idênticos. Ver `gtin.ts`.
   */
  gtin: string | null
  /**
   * O identificador da Amazon. **Não participa do casamento de linha**: ele não
   * vem impresso na embalagem nem num cupom fiscal. Serve para chegar na página
   * do produto.
   */
  asin: string | null
  imageUrl: string | null

  stats: ProductStats

  /** Aparece em "Monitorados por preço". */
  isMonitored: boolean
  /** Instante ISO 8601, ou `null`. */
  archivedAt: string | null

  createdAt: string
  updatedAt: string
}

/**
 * O resumo denormalizado, mantido por Function.
 *
 * **Separa o que PAGAMOS do que OBSERVAMOS**, e é a distinção que
 * `ProductPriceHistory.md` diz ser *"importante e fácil de errar"*: `purchase` é
 * dinheiro que saiu e alimenta "último preço pago" e a média; `manual`, `web` e
 * `nota_parana` são preço visto, e alimentam "menor preço encontrado". Misturar
 * os dois *"produz uma média histórica que não descreve nada"*.
 */
export type ProductStats = {
  /** Quantas COMPRAS — não quantos pontos. */
  purchaseCount: number
  /** O último preço PAGO, pela embalagem. */
  lastPriceCents: Cents | null
  lastPurchasedAt: CivilDate | null
  /** Média dos preços PAGOS, por unidade base. */
  avgPerBaseUnitMilliCents: MilliCents | null
  /**
   * O menor preço **vigente**, por unidade base — de qualquer fonte.
   *
   * "Vigente" e não "já visto": um ponto que a própria loja desmentiu depois
   * não conta (decisão 269). Ele continua no histórico, riscado; o que ele
   * perde é o direito de responder *"é ali que compensa"*, que é uma pergunta
   * sobre hoje.
   */
  minPerBaseUnitMilliCents: MilliCents | null
  minPriceMerchantId: string | null
  /** A base em que os dois números acima estão. `null` sem tamanho declarado. */
  baseUnit: BaseUnit | null
}

export const EMPTY_STATS: ProductStats = {
  purchaseCount: 0,
  lastPriceCents: null,
  lastPurchasedAt: null,
  avgPerBaseUnitMilliCents: null,
  minPerBaseUnitMilliCents: null,
  minPriceMerchantId: null,
  baseUnit: null,
}

export type PriceSource = 'purchase' | 'manual' | 'nota_parana' | 'web'

/**
 * Um ponto no tempo: este produto custou tanto, aqui, neste dia.
 *
 * **Imutável.** *"Um ponto de preço nunca é editado. Errou? Cria um ponto
 * corrigido e marca o anterior como superseded — nunca reescreve o passado."*
 * Princípio 7.
 */
export type PricePoint = {
  id: string
  householdId: string
  productId: string

  /** Preço pago pela unidade vendida. */
  priceCents: Cents
  quantity: number
  totalCents: Cents

  /** Milicentavos por unidade base. Ver `unit.ts` para por que não é centavo. */
  pricePerBaseUnitMilliCents: MilliCents
  baseUnit: BaseUnit

  merchantId: string | null
  /**
   * A página exata onde o preço foi visto, quando veio de um link. `null` para
   * preço de loja física — e para os que já existiam antes disto.
   *
   * O `merchantId` diz **onde**; este diz **em qual página**, que é o que
   * permite voltar lá e conferir.
   */
  sourceUrl: string | null
  observedAt: CivilDate

  source: PriceSource
  /**
   * Quem registrou. Outro eixo que `source`: aquele diz de onde veio o preço,
   * este diz quem o pôs aqui. Ver `recorded-by.ts`.
   */
  recordedBy: RecordedBy
  /** Uma anotação livre de quem registrou. */
  note: string | null
  /** Quando veio de compra nossa, com nota. */
  receiptItemId: string | null
  /** O ponto que este corrige. Nunca se reescreve o passado. */
  supersedesId: string | null
  /**
   * Quando alguém disse que este ponto não vale. `null` é o normal.
   *
   * **Anular não é apagar, e a diferença é o princípio 7.** O documento fica
   * no banco com quem anulou e quando; o que muda é que ele sai de toda soma
   * e da lista. É o que serve para o preço que está simplesmente errado e não
   * tem um certo para pôr no lugar — o caso que `supersedesId` não cobre,
   * porque aquele exige um ponto novo.
   */
  voidedAt: string | null
  /** Quem anulou. Sempre uma pessoa: anular é julgamento, não coleta. */
  voidedBy: RecordedBy | null

  createdAt: string
}
